import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

function MultiSelectDropdown({ 
  options = [], 
  selected = [], 
  onChange, 
  placeholder = "Select categories...",
  className = "" 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleOption = (optionId) => {
    const newSelected = selected.includes(optionId)
      ? selected.filter(id => id !== optionId)
      : [...selected, optionId];
    onChange(newSelected);
  };

  const removeOption = (optionId) => {
    const newSelected = selected.filter(id => id !== optionId);
    onChange(newSelected);
  };

  const getSelectedOptions = () => {
    return options.filter(option => selected.includes(option.id));
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected categories display */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {getSelectedOptions().map(option => (
            <div
              key={option.id}
              className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border"
              style={option.color ? { 
                backgroundColor: option.color + '20', 
                color: option.color,
                borderColor: option.color + '40'
              } : {}}
            >
              <span>{option.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeOption(option.id);
                }}
                className="ml-1 hover:bg-black/10 rounded-full p-0.5 transition-colors"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left border border-gray-300 rounded bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors flex items-center justify-between"
      >
        <span className="text-gray-700">
          {selected.length === 0 ? placeholder : `Выбрано категорий: ${selected.length}`}
        </span>
        <ChevronDown 
          size={16} 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">No categories available</div>
          ) : (
            <>
              {/* Select All / Clear All buttons */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-3 py-2 flex gap-2">
                <button
                  onClick={() => onChange(options.map(opt => opt.id))}
                  className="text-xs px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                >
                  Выбрать все
                </button>
                <button
                  onClick={() => onChange([])}
                  className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800 underline"
                >
                  Очистить
                </button>
              </div>

              {/* Options list */}
              {options.map(option => {
                const isSelected = selected.includes(option.id);
                
                return (
                  <div
                    key={option.id}
                    onClick={() => toggleOption(option.id)}
                    className={`px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between transition-colors ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}} // Handled by parent onClick
                        className="mr-2 rounded"
                      />
                      <span className="text-sm">{option.name}</span>
                      {option.color && (
                        <div 
                          className="ml-2 w-3 h-3 rounded-full border"
                          style={{ backgroundColor: option.color }}
                        />
                      )}
                    </div>
                    {option.itemCount !== undefined && (
                      <span className="text-xs text-gray-500">({option.itemCount})</span>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;